import { baseApiAuthenticated } from '@/api/v2/index';
import { buildSearchParams, urlBuilder } from '@/api/utils';
import { CardCode, CardCodePutBody } from '@/models/cardcodes';

const CARD_CODE_TAG = 'CARD_CODES';

const baseUrl = urlBuilder('order', 'cardcodes');

type GetCardCodesApiResponse = { cardCodes: CardCode[] };

export const cardCodesApi = baseApiAuthenticated
    .enhanceEndpoints({ addTagTypes: [CARD_CODE_TAG] })
    .injectEndpoints({
        endpoints: (builder) => ({
            getCardCodes: builder.query<CardCode[], { liftOperatorId: string }>(
                {
                    query: ({ liftOperatorId }) =>
                        `${baseUrl}${buildSearchParams({ liftOperatorId })}`,

                    transformResponse: (response: GetCardCodesApiResponse) =>
                        response.cardCodes,

                    providesTags: [CARD_CODE_TAG],
                }
            ),

            getSingleCardCode: builder.query<
                CardCode | null,
                { liftOperatorId: string; cardCodeId: string }
            >({
                query: ({ liftOperatorId }) =>
                    `${baseUrl}${buildSearchParams({ liftOperatorId })}`,

                transformResponse: (
                    response: GetCardCodesApiResponse,
                    _meta,
                    { cardCodeId }
                ) => {
                    return (
                        response.cardCodes?.find(
                            (singleCode) => singleCode.id === cardCodeId
                        ) || null
                    );
                },
            }),

            postCardCode: builder.mutation<
                void,
                { count: number; externalCode: string; liftOperatorId: string }
            >({
                query: (queryArg) => ({
                    url: baseUrl,
                    method: 'POST',
                    body: queryArg,
                }),

                invalidatesTags: [CARD_CODE_TAG],
            }),

            putCardCode: builder.mutation<void, CardCodePutBody>({
                query: (queryArg) => ({
                    url: `${baseUrl}/${queryArg.cardCodeId}`,
                    method: 'PUT',
                    body: queryArg,
                }),
            }),
        }),
    });

export const {
    useGetCardCodesQuery,
    useGetSingleCardCodeQuery,
    usePutCardCodeMutation,
    usePostCardCodeMutation,
} = cardCodesApi;
